"use client";
import React, { Suspense, useEffect } from "react";
import { usePathname } from "@/lib/router-compat";
import {
  SidebarInset,
  SidebarProvider,
  useSidebar,
} from "@/components/ui/sidebar";
import { Header } from "@/components/header";
import { AppSidebar } from "@/components/app-sidebar";
import { PageTransitionLayout } from "@/components/common/PageTransitionLayout";
import { GlobalLoadingOverlay } from "@/components/common/GlobalLoadingOverlay";
import { NavigationLoadingOverlay } from "@/components/common/NavigationLoadingOverlay";
import { FacilityProvider, useFacility } from "@/contexts/FacilityContext";
import { LoadingProvider } from "@/contexts/LoadingContext";
import { FacilityModeGuard } from "@/components/common/FacilityModeGuard";
import { PageChromeProvider } from "@/contexts/PageChromeContext";
import { OrganizationBrandingProvider } from "@/contexts/OrganizationBrandingContext";

// チャット画面ではサイドバーを閉じる制御コンポーネント
function SidebarController() {
  const { setOpen } = useSidebar();
  const pathname = usePathname();

  useEffect(() => {
    if (pathname?.startsWith("/chat")) {
      setOpen(false);
    }
    // setOpen は open 変化ごとに再生成されるため deps から除外し、
    // pathname 変更時のみ実行することでトグル操作を妨げない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return null;
}

function WorkspaceHeader() {
  const { selectedFacilityName, isAllFacilities } = useFacility();

  return (
    <Header
      facilityScope={{
        name: selectedFacilityName,
        isAllFacilities,
      }}
    />
  );
}

export default function AuthenticatedLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <LoadingProvider>
      <OrganizationBrandingProvider>
        <FacilityProvider>
          <PageChromeProvider>
            <SidebarProvider
              defaultOpen={true}
              className="h-dvh min-h-0 overflow-hidden"
            >
              <a
                href="#main-content"
                className="bg-background text-foreground focus-visible:ring-ring fixed left-3 top-3 z-[100] -translate-y-20 rounded-md border px-3 py-2 text-sm font-semibold shadow-lg transition-transform focus-visible:translate-y-0 focus-visible:outline-none focus-visible:ring-2 motion-reduce:transition-none"
              >
                メインコンテンツへ移動
              </a>
              <SidebarController />
              <AppSidebar />
              <SidebarInset
                id="main-content"
                tabIndex={-1}
                className="h-dvh min-h-0 min-w-0 overflow-hidden"
              >
                <WorkspaceHeader />
                <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
                  <div className="h-full min-h-0 min-w-0 overflow-auto">
                    <FacilityModeGuard>
                      <PageTransitionLayout>{children}</PageTransitionLayout>
                    </FacilityModeGuard>
                  </div>
                  {/* ナビゲーション中のローディングは本文領域のみを覆う。
                      ヘッダーとサイドバーは遷移中も操作可能なまま残す。 */}
                  <Suspense fallback={null}>
                    <NavigationLoadingOverlay />
                  </Suspense>
                </div>
              </SidebarInset>
            </SidebarProvider>
          </PageChromeProvider>
        </FacilityProvider>
      </OrganizationBrandingProvider>
      <GlobalLoadingOverlay />
    </LoadingProvider>
  );
}
